// Browsers silently strip ASCII control characters (tabs, newlines, carriage
// returns, etc.) before parsing a URL, so a raw string like "/\t/evil.com"
// can normalize into "//evil.com" (protocol-relative) even though it looks
// like a same-origin path before normalization. Checked by char code (not a
// regex literal) to keep the control-character range unambiguous and to avoid
// embedding raw control bytes in this source file.
function hasControlCharacter(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

function isSafeInternalRedirectShape(value: string): boolean {
  if (!value.startsWith("/")) return false;
  if (value.startsWith("//")) return false;
  if (value.startsWith("/\\")) return false;
  if (value.includes("://")) return false;
  return true;
}

/**
 * True only for a value that is safe to use as a post-authentication
 * redirect target on our own frontend: a relative, internal path.
 *
 * Mirrors `matchfeed-api/app/lib/url.ts`'s `isSafeInternalRedirect` exactly
 * (same rules, same bounded fixed-point decode loop) — deliberately
 * duplicated rather than shared, since no shared-code mechanism exists
 * between the two repos. If either implementation changes, the other must
 * be updated to match (see `team operations/human_confirmation.md`,
 * `[P4-4 FE half]`).
 *
 * Rejects (does not throw for) anything that could be interpreted by a
 * browser as pointing off-site:
 *  - not starting with exactly one `/` (bare paths, absolute URLs like
 *    `https://evil.com`, scheme URIs like `javascript:alert(1)`)
 *  - starting with `//` — protocol-relative, resolves against whatever
 *    origin follows, not ours
 *  - starting with `/\` — some browsers normalize a leading backslash to a
 *    slash, turning `/\evil.com` into `//evil.com`
 *  - containing `://` anywhere — catches an embedded absolute URL even past
 *    a leading slash (e.g. `/redirect?to=https://evil.com`)
 *  - containing ASCII control characters, which browsers strip before
 *    parsing and which can otherwise hide one of the above patterns
 *  - nested/multi-layer percent-encoding that decodes into any of the above
 *    at any layer (e.g. `/%2F%2Fevil.com` decodes to `//evil.com` in one
 *    pass; `/%252F%252Fevil.com` needs two passes; `/%09/evil.com` hides a
 *    literal tab that only appears after one decode) — decoding repeats to
 *    a fixed point (bounded, see below), re-checking control characters and
 *    shape at every intermediate value, not only the last one
 *
 * A value that is still changing after `MAX_DECODE_ITERATIONS` decode
 * passes is rejected outright rather than accepted on a guess — legitimate
 * redirect targets never need that much nesting.
 *
 * Callers should treat a `false` result as "ignore the redirect, not as an
 * error" — a malformed or malicious redirect must never block whatever
 * legitimate action (e.g. logging in) it was attached to. `redirect` is
 * only ever used to choose the `navigate()` target — it is never trusted
 * for anything authorization-relevant, so it cannot bypass whatever guard
 * the destination route already enforces (see `src/middleware.ts`).
 */
const MAX_DECODE_ITERATIONS = 5;

export function isSafeInternalRedirect(value: string): boolean {
  if (typeof value !== "string" || value.length === 0) return false;

  let current = value;

  for (let i = 0; i < MAX_DECODE_ITERATIONS; i++) {
    if (hasControlCharacter(current)) return false;
    if (!isSafeInternalRedirectShape(current)) return false;

    let decoded: string;
    try {
      decoded = decodeURIComponent(current);
    } catch {
      return false; // malformed percent-encoding — reject rather than guess
    }

    // Fixed point: decoding no longer changes the value, so every layer of
    // encoding has been unwrapped and validated above. Safe to accept.
    if (decoded === current) return true;

    current = decoded;
  }

  // Still changing after MAX_DECODE_ITERATIONS passes — reject rather than
  // risk missing a bypass hidden behind deeper nesting than we checked.
  return false;
}
